import { Project } from '../types/project';
import { CalculationResults } from './calculations';
import { REMOVED_LOAD_RATE } from './calc/methods/measured22087';
import { getEditionPack, resolvedEditionKey } from './calc/editions';

// What the report says about equipment taken out with this work. One builder
// for both report renderers, like newLoadFactorDisclosure.ts. Null when no
// removed equipment is listed.
//
// Three cases, decided 2026-10-13 (context/nec-methodology.md, "Removed load"):
//   traditional, any edition — not counted: the optional method calculates the
//     dwelling as it will be after the work, so removal is omission, not credit;
//   measured, 2026 — subtracted at 40% of rating after the 125%, zero for an
//     item that does not run in the peak's season;
//   measured, 2020/2023 — listed, never credited: the section has no
//     subtraction, and the metered peak already contains the removed load.

const fmt = (n: number) => n.toFixed(2);

export function removedLoadDisclosure(
  project: Partial<Project>,
  results: CalculationResults,
  method: 'measured' | 'traditional',
  cite: string
): string | null {
  if (results.removedCalculations.length === 0) return null;
  const pack = getEditionPack(resolvedEditionKey(project));

  if (method === 'traditional') {
    const section = pack.sectionMap.existingDwelling ?? cite;
    return (
      `Equipment listed as removed with this work is not counted. NEC ${section} calculates the load of the dwelling ` +
      `as it will be after the proposed work, so equipment being taken out is left out of the calculation rather ` +
      `than subtracted from it.`
    );
  }

  if (!results.removedLoadCreditApplied) {
    return (
      `The equipment listed as removed is shown for the record and is not credited. NEC ${cite} in the ` +
      `${pack.edition} edition adds the new load to the measured demand and provides no subtraction for removed ` +
      `load. The measured demand already includes whatever the removed equipment drew, so this evaluation is ` +
      `conservative to that extent.`
    );
  }

  return (
    `Under the ${pack.edition} edition, NEC ${cite} Condition (2) subtracts any removed load, determined in ` +
    `accordance with Article 120 Parts I, III, and IV. The section states the subtraction but not how the removed ` +
    `load is valued. This evaluation credits each removed item at ${Math.round(REMOVED_LOAD_RATE * 100)} percent of ` +
    `its rating, the rate Table 120.83 gives existing load beyond the first ${pack.optional83TierKVA} kVA, and ` +
    `subtracts it after the 125 percent factor, in the order the section states. An item not expected to operate in ` +
    `the season of the measured peak contributed nothing to that peak and is credited at zero. Total credit: ` +
    `${fmt(results.totalRemovedCreditKVA)} kVA. The valuation is TrueLoad's reading, not a determination the Code ` +
    `makes, and the authority having jurisdiction may require otherwise.`
  );
}
